import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

type LegalPageType = 'privacy' | 'terms' | 'cookies';

// Metadata type definition
interface Metadata {
  title: string;
  lastUpdated: string;
}

interface MetadataCollection {
  [key: string]: Metadata;
}

interface LegalPageProps {
  type?: LegalPageType;
}

function LegalPage({ type = 'privacy' }: LegalPageProps) {
  const params = useParams();
  const pageType = (params.type as LegalPageType) || type;
  
  const [content, setContent] = useState<string>('');
  const [metadata, setMetadata] = useState<Metadata>({ title: '', lastUpdated: '' });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Function to fetch the markdown content
    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch the markdown file
        const markdownResponse = await fetch(`/legal/${pageType}.md`);
        if (!markdownResponse.ok) {
          throw new Error(`Failed to load ${pageType} content`);
        }
        const markdownText = await markdownResponse.text();
        
        // Fetch metadata
        const metadataResponse = await fetch('/legal/metadata.json');
        if (!metadataResponse.ok) {
          throw new Error('Failed to load metadata');
        }
        const metadataJson = await metadataResponse.json() as MetadataCollection;
        
        setContent(markdownText);
        setMetadata(metadataJson[pageType] || { title: '', lastUpdated: '' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        console.error('Error loading legal content:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
  }, [pageType]);
  
  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="container">
          <Link to="/" className="back-to-home">
            ← Back to Home
          </Link>
          <h1>{metadata.title}</h1>
          <p className="last-updated">{metadata.lastUpdated}</p>
        </div>
      </div>
      
      <div className="legal-content container">
        {loading && <p className="loading">Loading...</p>}
        
        {error && (
          <div className="error-message">
            <p>Sorry, we couldn't load the content. {error}</p>
            <p>Please try again later or contact support.</p>
          </div>
        )}
        
        {!loading && !error && (
          <div className="legal-text">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

export default LegalPage; 